import type { GroupedTasks } from '../../../lib/dailyApi';
import { splitMineVsTeam } from '../../../utils/taskGrouping';
import { TaskGroup } from './TaskGroup';
import { useI18n } from '../../../contexts/I18nContext';

interface Props {
  grouped: GroupedTasks;
  currentUserId: string;
}

const BUCKET_LABEL_KEYS: Record<keyof GroupedTasks, string> = {
  overdue: 'daily.bucket.overdue',
  today: 'daily.bucket.today',
  tomorrow: 'daily.bucket.tomorrow',
  next: 'daily.bucket.next',
};

/**
 * Tasks section for the daily summary (TASK-FE-DAILY-004). Renders each due
 * bucket, splitting "My Tasks" from "Team Tasks" and showing due dates for the
 * "next" bucket.
 */
export function TasksSection({ grouped, currentUserId }: Props) {
  const { t } = useI18n();
  const buckets: (keyof GroupedTasks)[] = ['overdue', 'today', 'tomorrow', 'next'];
  const total = buckets.reduce((sum, b) => sum + grouped[b].length, 0);

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="font-semibold text-gray-800">{t('daily.tasks')}</h2>
      {total === 0 ? (
        <p className="mt-2 text-sm text-gray-500">{t('daily.tasks.empty')}</p>
      ) : (
        buckets.map((bucket) => {
          const { mine, team } = splitMineVsTeam(grouped[bucket], currentUserId);
          if (mine.length === 0 && team.length === 0) return null;
          const tone = bucket === 'overdue' ? 'overdue' : 'plain';
          return (
            <div key={bucket} className="mt-4">
              <p
                className={`text-xs font-bold uppercase tracking-wider ${
                  bucket === 'overdue' ? 'text-red-600' : 'text-blue-600'
                }`}
              >
                {t(BUCKET_LABEL_KEYS[bucket])}
              </p>
              <TaskGroup title={t('daily.tasks.mine')} tasks={mine} tone={tone} />
              <TaskGroup title={t('daily.tasks.team')} tasks={team} tone={tone} />
            </div>
          );
        })
      )}
    </section>
  );
}
